import { useGetMyAgencies } from '@frontend/features/agencies/queries';
import { Fragment, useEffect, useState } from 'react';
import { HotelReservationDialog } from '@frontend/features/hotels/HotelReservationDialog';
import { useBookingNotificationTarget } from '@frontend/features/travel-services/hooks/useBookingNotificationTarget';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Badge,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tab,
  Tabs,
  Typography,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import {
  RuteroBookingListSort,
  RuteroBookingListSortDirection,
  RuteroBookingListTab,
} from '@bandido/shared/types';

import { CONFIG } from '@frontend/config';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import {
  TravelBooking,
  useSyncBooking,
  useTravelBookingRefresh,
  useTravelBookings,
} from '@frontend/features/travel-services/queries';
import { BookingDetailModal } from '@frontend/features/travel-services/components/BookingDetailModal';
import {
  earliestActiveOperationRecheckBooking,
  useActiveOperationRecheck,
} from '@frontend/features/travel-services/activeOperationRecheck';
import {
  BOOKING_LIST_COLUMNS,
  BOOKING_LIST_TABLE_WIDTH,
  BOOKING_LIST_TABS,
  bookingListAdjacentTab,
  bookingListEmptyState,
  bookingListTabBadge,
  bookingListTabGroupsByDay,
  bookingListTabPaginates,
  bookingListTabSupportsSort,
} from '@frontend/features/travel-services/bookingList';
import {
  BookingListFilters,
  BookingListSortState,
  EMPTY_BOOKING_LIST_FILTERS,
  activeBookingListFilterCount,
  bookingCaseFilterFromSearch,
  bookingListQueryParams,
  hasActiveBookingListFilters,
} from '@frontend/features/travel-services/bookingListFilters';
import {
  BookingListScope,
  MINE_BOOKING_LIST_SCOPE,
  defaultBookingListScope,
  loadBookingListScope,
  persistBookingListScope,
  resolveBookingListScope,
} from '@frontend/features/travel-services/bookingListScope';
import {
  groupBookingsByServiceDay,
  serviceDayLabel,
  todayKey,
} from '@frontend/features/travel-services/bookingListDays';
import { bookingListAction } from '@frontend/features/travel-services/bookingListRow';
import {
  BookingDayHeader,
  BookingListCard,
  BookingListRow,
} from '@frontend/features/travel-services/components/BookingListRow';
import { BookingListFiltersBar } from '@frontend/features/travel-services/components/BookingListFiltersBar';
import { BookingListScopeSelector } from '@frontend/features/travel-services/components/BookingListScopeSelector';

/** Los órdenes que ofrece el contrato, en las dos direcciones. */
const SORT_CHOICES: { sort: RuteroBookingListSort; dir: RuteroBookingListSortDirection }[] = [
  { sort: 'cancellationPenaltyStartsAt', dir: 'asc' },
  { sort: 'cancellationPenaltyStartsAt', dir: 'desc' },
  { sort: 'createdAt', dir: 'desc' },
  { sort: 'createdAt', dir: 'asc' },
  { sort: 'serviceDate', dir: 'asc' },
  { sort: 'serviceDate', dir: 'desc' },
  { sort: 'finalAmount', dir: 'desc' },
  { sort: 'finalAmount', dir: 'asc' },
];

const sortValue = (state: BookingListSortState) => `${state.sort}:${state.dir}`;

/**
 * La cola de trabajo del operador: cuatro pestañas que son cuatro TRABAJOS,
 * un ámbito que manda sobre todo (contadores incluidos) y una fila que lleva
 * SU acción — la misma matriz que el detalle.
 *
 * Todo sale de la tabla LOCAL: el hub no tiene endpoint de listado, así que
 * bandido es el sistema de registro de esta pantalla.
 */
export const TravelBookingsList = () => {
  const { t, i18n } = useTranslation();
  const { addToast } = useSnackBar();
  const { myProfile } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchParams, setSearchParams] = useSearchParams();
  const myAgencies = useGetMyAgencies({});
  const selectedAgency = searchParams.get('agencyId') ?? 'all';
  const agencyId = selectedAgency === 'all' ? 'all' : Number(selectedAgency);
  const agencyAllowed = !myAgencies.isError && (agencyId === 'all'
    ? Boolean(myAgencies.data?.length)
    : myAgencies.data?.some((agency) => agency.id === agencyId) === true);

  const hotelReservationId = searchParams.get('hotelReservationId');
  const bookingId = searchParams.get('bookingId');
  const linkedBooking = useBookingNotificationTarget(bookingId, agencyId === 'all' ? Number(CONFIG.AGENCY_ID) : agencyId, myProfile?.id);
  const closeLinkedBooking = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('bookingId');
    setSearchParams(next, { replace: true });
  };
  const [tab, setTab] = useState<RuteroBookingListTab>(
    hotelReservationId || bookingCaseFilterFromSearch(searchParams).caseId ? 'all' : 'attention',
  );
  const [scope, setScope] = useState<BookingListScope>(MINE_BOOKING_LIST_SCOPE);
  const [filters, setFilters] = useState<BookingListFilters>(() => bookingCaseFilterFromSearch(searchParams));
  const [sort, setSort] = useState<BookingListSortState | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [detail, setDetail] = useState<TravelBooking | null>(null);
  const [openManual, setOpenManual] = useState(false);
  const caseIdFromUrl = bookingCaseFilterFromSearch(searchParams).caseId;
  /**
   * CUÁL fila se está sincronizando. Con un booleano de la mutación, todas las
   * filas sincronizables giran a la vez y la pantalla diría que se están
   * sincronizando cuatro reservas cuando se disparó una.
   */
  const [syncingId, setSyncingId] = useState<string | null>(null);

  /**
   * El ámbito con el que entra cada usuario: lo último que eligió, o el
   * default de su rol. Va por usuario porque la app no recarga al cambiar de
   * sesión — sin eso, el que entra después heredaría el ámbito del anterior.
   */
  useEffect(() => {
    if (!myProfile?.id) return;
    setScope(
      loadBookingListScope(myProfile.id) ??
        defaultBookingListScope(myProfile.roles),
    );
  }, [myProfile?.id, myProfile?.roles]);

  // La sidebar y el historial pueden cambiar sólo el query string sin
  // desmontar esta pantalla. En ese caso el filtro visible debe seguir a la
  // URL (incluido volver desde `?caseId=…` al listado general).
  useEffect(() => {
    setFilters((current) =>
      current.caseId === caseIdFromUrl
        ? current
        : { ...current, caseId: caseIdFromUrl },
    );
    if (caseIdFromUrl) setTab('all');
    setPage(1);
  }, [caseIdFromUrl]);

  const scopeResolution = resolveBookingListScope(scope, myProfile?.id);
  const isScopeReady = scopeResolution.status === 'ready' && agencyAllowed;
  const params = bookingListQueryParams({
    tab,
    createdBy: isScopeReady ? scopeResolution.createdBy : undefined,
    filters,
    sort,
    page,
  });

  const query = useTravelBookings(agencyId, params, { enabled: isScopeReady, userId: myProfile?.id });
  /**
   * Mientras el ámbito no se resuelve no se dibuja NADA de la cache: la key
   * de un "Mías" sin usuario cargado coincide con la de "Todas", y servir esa
   * respuesta mostraría las reservas de toda la agencia bajo un selector que
   * dice "Mías" (§21.2).
   */
  const data = isScopeReady && !query.isPreviousData ? query.data : undefined;
  const sync = useSyncBooking();
  const refreshBooking = useTravelBookingRefresh();
  useActiveOperationRecheck(earliestActiveOperationRecheckBooking(data?.data));

  const today = todayKey(new Date());
  const bookings = data?.data ?? [];
  const hasFilters = hasActiveBookingListFilters(filters);
  const isStale = query.isPreviousData || !isScopeReady;

  const changeScope = (next: BookingListScope) => {
    setScope(next);
    persistBookingListScope(myProfile?.id, next);
    setPage(1);
  };

  const changeTab = (next: RuteroBookingListTab) => {
    setTab(next);
    setPage(1);
    // El orden pedido es de la pestaña que lo ofrece: al volver a una cola,
    // el orden que manda es el suyo.
    if (!bookingListTabSupportsSort(next)) setSort(undefined);
  };

  const changeFilters = (next: BookingListFilters) => {
    setFilters(next);
    const nextSearch = new URLSearchParams(searchParams);
    if (next.caseId) nextSearch.set('caseId', String(next.caseId));
    else nextSearch.delete('caseId');
    setSearchParams(nextSearch, { replace: true });
    setPage(1);
  };

  /**
   * El texto llega con retraso: se mezcla con los filtros VIGENTES al momento
   * de aplicarlo, no con los que había cuando se tipeó.
   */
  const changeSearch = (text?: string) => {
    setFilters((current) => ({ ...current, q: text }));
    setPage(1);
  };

  const openDetail = (booking: TravelBooking, manual = false) => {
    setOpenManual(manual);
    setDetail(booking);
  };

  const handleSync = async (booking: TravelBooking) => {
    if (booking.source === 'hotelrs') {
      openDetail(booking);
      return;
    }
    setSyncingId(booking.id);
    try {
      await sync.mutateAsync(booking.id);
    } catch {
      addToast({ message: t('travelServices.booking.syncError'), color: 'error' });
      await refreshBooking(booking.id, (updated) => {
        setDetail((current) => (current?.id === updated.id ? updated : current));
      });
    } finally {
      setSyncingId(null);
    }
  };

  const onRowOpen = (booking: TravelBooking) =>
    openDetail(booking, bookingListAction(booking).kind === 'manual');

  const rowProps = {
    today,
    locale: i18n.language,
    onOpen: onRowOpen,
    onSync: handleSync,
    syncingId,
    isStale,
  };

  const toggleSort = (column: RuteroBookingListSort) => {
    setSort((current) => (current?.sort === column
      ? { sort: column, dir: current.dir === 'asc' ? 'desc' : 'asc' }
      : { sort: column, dir: column === 'serviceDate' ? 'asc' : 'desc' }));
    setPage(1);
  };

  const empty = bookingListEmptyState({ tab, hasFilters });
  const emptyBlock = (
    <Alert severity={empty.tone === 'success' ? 'success' : 'info'} sx={{ mt: 2 }}>
      <AlertTitle>{t(empty.titleKey)}</AlertTitle>
      {t(empty.bodyKey)}
      {empty.action === 'clearFilters' && (
        <Box mt={1}>
          <Button
            size="small"
            onClick={() => changeFilters(EMPTY_BOOKING_LIST_FILTERS)}
          >
            {t('travelServices.cars.clearFilters')}
          </Button>
        </Box>
      )}
    </Alert>
  );

  /** Filas y encabezados de día: "próximas" es la única que se lee por día. */
  const groups = bookingListTabGroupsByDay(tab)
    ? groupBookingsByServiceDay(bookings, today)
    : [{ dateKey: '', kind: 'date' as const, bookings }];

  const dayLabel = (group: (typeof groups)[number]) => {
    if (group.kind === 'today') return t('travelServices.bookingList.day.today');
    if (group.kind === 'tomorrow') {
      return t('travelServices.bookingList.day.tomorrow');
    }
    if (group.kind === 'undated') {
      return t('travelServices.bookingList.day.undated');
    }
    return serviceDayLabel(group.dateKey, i18n.language, 'long') ?? group.dateKey;
  };

  return (
    <Box p={{ xs: 1.5, sm: 3 }}>
      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={2}
      >
        <Box>
          <Typography variant="h5">{t('travelServices.bookingsTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('travelServices.bookingsSubtitle')}
          </Typography>
        </Box>
        <TextField select size="small" label={t('hotelSharing.salesAgency')} value={agencyAllowed ? agencyId : ''}
          disabled={myAgencies.isLoading}
          onChange={(event) => {
            const next = new URLSearchParams(searchParams);
            next.set('agencyId', event.target.value); next.delete('caseId'); next.delete('hotelReservationId'); next.delete('bookingId');
            setPage(1); setFilters({}); setScope(MINE_BOOKING_LIST_SCOPE); setDetail(null);
            setSearchParams(next, {replace:true});
          }}>
          <MenuItem value="all">{t('travelServices.bookingList.scope.all')}</MenuItem>
          {myAgencies.data?.map((agency) => <MenuItem key={agency.id} value={agency.id}>{agency.name}</MenuItem>)}
        </TextField>
        <BookingListScopeSelector
          agencyIds={agencyId === 'all' ? (myAgencies.data ?? []).map((agency) => agency.id) : agencyAllowed ? [agencyId] : []}
          scope={scope}
          onChange={changeScope}
        />
      </Box>

      <Tabs
        value={tab}
        onChange={(_event, value) => changeTab(value as RuteroBookingListTab)}
        variant="scrollable"
        scrollButtons="auto"
        onKeyDown={(event) => {
          // Las flechas mueven la pestaña ABIERTA, no solo el foco: la cola
          // se recorre con una mano.
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          changeTab(bookingListAdjacentTab(tab, event.key === 'ArrowRight' ? 1 : -1));
        }}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        {BOOKING_LIST_TABS.map((spec) => {
          const badge = bookingListTabBadge(spec, data?.meta?.tabCounts);
          return (
            <Tab
              key={spec.tab}
              value={spec.tab}
              label={
                badge ? (
                  <Badge color="warning" badgeContent={badge} sx={{ pr: 1.5 }}>
                    {t(spec.labelKey)}
                  </Badge>
                ) : (
                  t(spec.labelKey)
                )
              }
            />
          );
        })}
      </Tabs>

      <BookingListFiltersBar
        filters={filters}
        onChange={changeFilters}
        onSearchChange={changeSearch}
        onClear={() => changeFilters(EMPTY_BOOKING_LIST_FILTERS)}
        activeCount={activeBookingListFilterCount(filters)}
        resultCount={bookings.length}
        providerFacets={query.isPreviousData ? undefined : data?.meta?.providerFacets}
        sort={
          bookingListTabSupportsSort(tab)
            ? {
              value: sort ?? { sort: 'createdAt', dir: 'desc' },
              options: SORT_CHOICES.map((choice) => ({
                value: sortValue(choice),
                label: t(
                  `travelServices.bookingList.sort.${choice.sort}.${choice.dir}`,
                ),
              })),
              onChange: (value) => {
                const [column, dir] = value.split(':');
                setSort({
                  sort: column as RuteroBookingListSort,
                  dir: dir as RuteroBookingListSortDirection,
                });
                setPage(1);
              },
            }
            : undefined
        }
      />

      {query.isLoading && isScopeReady && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}
      {query.isError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t('travelServices.searchError')}
        </Alert>
      )}
      {/* La cola sin paginar vino recortada: se dice, nunca se recorta en
          silencio. */}
      {data?.meta?.truncated && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {t('travelServices.bookingList.truncated', {
            count: data.totalRecords,
            shown: bookings.length,
          })}
        </Alert>
      )}

      {data && (
        <Box mt={2} aria-busy={isStale}>
          {isStale && <LinearProgress />}
          {bookings.length === 0 && emptyBlock}

          {bookings.length > 0 && isMobile && (
            <Stack spacing={1.5} sx={{ opacity: isStale ? 0.6 : 1 }}>
              {groups.map((group) => (
                <Stack key={group.dateKey || 'all'} spacing={1.5}>
                  {bookingListTabGroupsByDay(tab) && (
                    <Typography variant="overline" color="text.secondary">
                      {dayLabel(group)}
                    </Typography>
                  )}
                  {group.bookings.map((booking) => (
                    <BookingListCard key={booking.id} booking={booking} {...rowProps} />
                  ))}
                </Stack>
              ))}
            </Stack>
          )}

          {bookings.length > 0 && !isMobile && (
            <Box sx={{ overflowX: 'auto', opacity: isStale ? 0.6 : 1 }}>
              <Table size="small" sx={{ tableLayout: 'fixed', minWidth: BOOKING_LIST_TABLE_WIDTH }}>
                <TableHead>
                  <TableRow>
                    {BOOKING_LIST_COLUMNS.map((column) => (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        sx={{
                          boxSizing: 'border-box',
                          width: column.width,
                          minWidth: column.width,
                        }}
                      >
                        {column.sort && bookingListTabSupportsSort(tab) ? (
                          <TableSortLabel
                            active={sort?.sort === column.sort}
                            direction={
                              sort?.sort === column.sort ? sort.dir : 'desc'
                            }
                            onClick={() =>
                              toggleSort(column.sort as RuteroBookingListSort)
                            }
                          >
                            {t(column.labelKey)}
                          </TableSortLabel>
                        ) : (
                          t(column.labelKey)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groups.map((group) => (
                    <Fragment key={group.dateKey || 'all'}>
                      {bookingListTabGroupsByDay(tab) && (
                        <BookingDayHeader
                          label={dayLabel(group)}
                          columns={BOOKING_LIST_COLUMNS.length}
                        />
                      )}
                      {group.bookings.map((booking) => (
                        <BookingListRow
                          key={booking.id}
                          booking={booking}
                          {...rowProps}
                        />
                      ))}
                    </Fragment>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {bookingListTabPaginates(tab) && data.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={data.totalPages}
                page={page}
                onChange={(_event, value) => setPage(value)}
              />
            </Box>
          )}
        </Box>
      )}

      {hotelReservationId && (
        <HotelReservationDialog
          key={hotelReservationId}
          reservationId={hotelReservationId}
          onClose={() => {
            const next = new URLSearchParams(searchParams);
            next.delete('hotelReservationId');
            setSearchParams(next, { replace: true });
          }}
        />
      )}
      {bookingId && linkedBooking.isFetching && !linkedBooking.data && <LinearProgress />}
      {bookingId && (linkedBooking.invalidId || linkedBooking.isError) && (
        <Alert severity="warning" onClose={closeLinkedBooking}>
          {t('travelServices.updates.unavailable')}
        </Alert>
      )}
      <BookingDetailModal
        booking={bookingId ? linkedBooking.data ?? null : detail}
        openManualReconciliation={bookingId ? false : openManual}
        onClose={() => { setDetail(null); if (bookingId) closeLinkedBooking(); }}
      />
    </Box>
  );
};

export default TravelBookingsList;
